<script lang="ts">
    import { TextFragmentUtils } from "./utils/TextFragmentUtils.js";
    import { onMount, createEventDispatcher } from "svelte";
    import { LocalStorage } from "./services/LocalStorage";
    import ErrorBoundary, {
        SentryEnvironment,
    } from "./components/errorBoundary/ErrorBoundary.svelte";
    import HighlighterModal from "./components/highlighter/HighlighterModal.svelte";
    import { fade } from "svelte/transition";
    import { removeSuggestionList } from "./components/editor/utils";
    import {
        createQuery,
        getContainer,
    } from "./utils/CustomLocateV4/query/index.js";
    import { debounce } from "./utils/Utils.js";
    import { SnippetCreatedModalStore } from "./components/highlighter/SnippetCreatedModalStore";
    import SnippetCreatedModal from "./components/highlighter/SnippetCreatedModal.svelte";
    import { KnowledgeTriggerStore} from "$/stores/KnowledgeTriggerStore"
    import { visualizeSnippets } from "$/utils/CustomLocateV4/marker/index";
    import HighlightOptions from "./components/highlightOptions/HighlightOptions.svelte";
    import { PreferencesStore, PreferencesStoreInitialized } from "$/stores/global/PreferencesStore";
    import { cleanupAllTriggers } from "./utils/CustomLocateV4/marker/utils.js";
    import { HighlightStore } from "$/stores/HighlightStore";


    export let location: Location = window.location;
    export let showSnippetCreated: boolean = true;

    let highlight = null;

    let dispatch = createEventDispatcher();

    let textSelected: boolean = false;
    let buttonPosition: { top: number; left: number } = { top: 0, left: 0 };
    let hideHighlighterButton: boolean = false;
    let showHighlighterModal: boolean = false;
    let knowledgeTriggers = [];

    const isElementTextSelectable = (range: Range) => {
        // Check if any of the parent elements of the selection is input, text area or contains contentEditable attribute
        const isInput =
            range.startContainer.parentElement.closest("input") ||
            document.activeElement?.tagName === "INPUT";
        const isTextArea =
            range.startContainer.parentElement.closest("textarea") ||
            document.activeElement?.tagName === "TEXTAREA";
        const isContentEditable =
            range.startContainer.parentElement.closest(
                "[contenteditable='true']",
            ) ||
            document.activeElement?.getAttribute("contenteditable") === "true";
        const isSelectionFromOurHighlighter =
            document.activeElement.classList.contains(
                "js-collabwriting-highlighter",
            );

        // Return false if any of the above conditions are true
        return (
            !isInput &&
            !isTextArea &&
            !isContentEditable &&
            !isSelectionFromOurHighlighter
        );
    };

    const shouldHideHighlighterButton = () => {
        // First check if .js-collabwriting-app is present in the DOM
        if (document.querySelector(".js-collabwriting-app")) {
            // If present, then we do not need to show the button
            hideHighlighterButton = true;
        } else if (chrome?.storage) {
            // Check from our storage, if the user has set the preference to hide the button
            chrome.storage?.local.get(["preferences"], (result) => {
                if (chrome.runtime.lastError) {
                    // Handle storage error
                    console.error("Storage error:", chrome.runtime.lastError);
                    return;
                }

                // if hideHighlighterButton is true, then we do not need to add the button
                hideHighlighterButton =
                    result?.preferences?.hideHighlighterButton;
            });
        }
    };

    const isStartOfSelectionTextNode = (range: Range) => {
        // get node of the start of the selection
        let startNode = range.startContainer;

        if (startNode.nodeType === Node.TEXT_NODE) {
            return true;
        }

        return false;
    };

    // NOTE: We must generate the highlight object here
    // because the selection object is lost when the modal is opened
    const generateHighlight = (selection) => {
        let highlight;

        if (selection.anchorNode && selection.focusNode) {
            highlight = {
                anchorNode: createQuery(selection.anchorNode),
                anchorOffset: selection.anchorOffset,
                focusNode: createQuery(selection.focusNode),
                focusOffset: selection.focusOffset,
                pageNumber: null,
                container: createQuery(getContainer(selection)),
            };
        }

        const { preContent, postContent } =
            TextFragmentUtils.generatePreAndPostContent(selection);
        highlight.preContent = preContent;
        highlight.postContent = postContent;

        return highlight;
    };

    const measureStartSelectionPosition = (range: Range) => {
        let startNode = range.startContainer;
        let startOffset = range.startOffset;
        let endNode = range.endContainer;
        let endOffset = range.endOffset;

        let buttonPosition = { top: 0, left: 0 };

        // check if the start of the selection is a text node
        if (startNode.nodeType === Node.TEXT_NODE) {
            // create a temporary range to measure the position
            let tempRange = document.createRange();
            tempRange.setStart(startNode, startOffset);
            tempRange.setEnd(endNode, endOffset);

            let rect = tempRange.getBoundingClientRect();
            buttonPosition = {
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX + rect.width / 2 - 60,
            };
        }

        return buttonPosition;
    };

    const calculateButtonPosition = () => {
        let selection = window.getSelection() || null;

        if (!selection || !selection.toString().trim()) {
            textSelected = false;
            return;
        }

        let range = selection?.getRangeAt(0) || null;
        let buttonTop = 0;
        let buttonLeft = 0;

        // Check if the selected text is not inside input, text area or content editable
        if (!isElementTextSelectable(range)) {
            textSelected = false;
            return;
        }

        // Calculate correct position for the button according to the selection
        // Check if the start of the selection is the start of the text node
        if (!isStartOfSelectionTextNode(range)) {
            buttonTop =
                range.getBoundingClientRect().bottom + window.scrollY + 8;
            buttonLeft =
                range.getBoundingClientRect().left +
                window.scrollX +
                range.getBoundingClientRect().width / 2 -
                60;
        } else {
            // If the start of the selection is the start of the text node
            //   then we need to measure the position of the start of the selection
            let position = measureStartSelectionPosition(range);
            buttonTop = position.top;
            buttonLeft = position.left;
        }

        // Set correct position for the button
        buttonPosition = {
            top: buttonTop,
            left: buttonLeft,
        };
    };

    const isSelectionElementPresent = (element: HTMLElement) => {
        if (!element) return false;
        return document.body.contains(element);
    };

    const handleTextSelection = () => {
        const selection = window.getSelection();
        textSelected = !!selection?.toString()?.trim();

        if (
            textSelected &&
            isSelectionElementPresent(
                selection?.getRangeAt(0).startContainer.parentElement,
            )
        ) {
            calculateButtonPosition();
        }
    };

    const debouncedTextSelection = debounce(handleTextSelection, 300, false);

    document.onmouseup = debouncedTextSelection;
    document.onkeyup = debouncedTextSelection;
    document.onpointerup = debouncedTextSelection;
    document.onclick = debouncedTextSelection;

    // TODO: Try with on touch end + interval to check if the selection is still there
    let onSelectionTimer;
    document.onselectionchange = () => {
        /**
         * NOTE: The debounce timer is added because on mobile phones touch and selection
         *       events weren't working as expected. But when onselection is used then user
         *       when quickly selecting a peace of text was able to go over the highlighter
         *       button which would then scroll the page to the bottom.
         */
        clearTimeout(onSelectionTimer);
        textSelected = false;
        onSelectionTimer = setTimeout(() => {
            textSelected = !!document.getSelection()?.toString()?.trim();
            // HACK: check it there's more then 2 words selected. This is a hack to prevent the
            //       button from showing on the initial mobile long-press.
            if (
                document.getSelection()?.toString()?.trim().split(" ").length <
                2
            ) {
                textSelected = false;
            }

            if (!textSelected) return;
            else calculateButtonPosition();
        }, 200);
    };

    // Create a MutationObserver to check if the user
    // has enabled the hideHighlighterButton preference
    // or if the .js-collabwriting-app is present in the DOM
    const observer = new MutationObserver(() => {
        shouldHideHighlighterButton();
    });


    $: {
        if ($PreferencesStore) {
            (async () => {
                await initializeKnowledgeTriggers();
            })();
        }
    }

    onMount( ()  => {
        // Initial check to see if the button should be hidden
        shouldHideHighlighterButton();
        // Then start observing the document for changes
        observer.observe(document, { childList: true, subtree: true });
    });

    let shouldRenderHighlighterButton: boolean = false;
    $: {
        shouldRenderHighlighterButton = textSelected && !hideHighlighterButton;
        if (shouldRenderHighlighterButton) {
            LocalStorage.set("highlighterButtonShown", true);
        } else {
            LocalStorage.set("highlighterButtonShown", false);
        }
    }

    let shortcutText = navigator.platform.includes("Mac")
        ? "⌥ + ⇧ + S"
        : "ALT + SHIFT + S";

    // if the extension is installed await for chrome shortcut to be invoked
    if (chrome?.runtime?.onMessage) {
        chrome.runtime.onMessage.addListener((msg, sender, sendResponse) => {
            if (msg.action === "highligherShortcutActivated") {
                const selection: Selection = window.getSelection();
                const isSelectionEmpty: boolean =
                    !selection ||
                    selection.type === "None" ||
                    selection.toString().trim() === "";
                const isElementSelectable: boolean = !isSelectionEmpty
                    ? isElementTextSelectable(selection.getRangeAt(0))
                    : false;

                // generate highlight if the element is selectable, otherwise set to null so user creates a bookmark
                highlight = isElementSelectable
                    ? generateHighlight(selection)
                    : null;

                showHighlighterModal = true;
            }

            if (msg.action == "triggerHighlighterFromContextMenu") {
                highlight = generateHighlight(window.getSelection());
                window.getSelection().removeAllRanges();
                showHighlighterModal = true;
            }
        });

        // if the extension is not installed add the event listener to the document
    } else {
        document.addEventListener("keydown", (e) => {
            if (e.key === "S" && e.altKey && e.shiftKey) {
                highlight = generateHighlight(window.getSelection());
                showHighlighterModal = true;
            }
        });
    }
    function showHighlighter(eventOrParams = null) {
    if (eventOrParams instanceof MouseEvent || eventOrParams === null) {
        highlight = {
            ...generateHighlight(window.getSelection()),
            content: window.getSelection().toString().trim() || "",
        };
    } else {
        const highlightData = eventOrParams.detail.highlight;

        highlight = {
            anchorNode: highlightData.anchorNode,
            anchorOffset: highlightData.anchorOffset || 0,
            focusNode: highlightData.focusNode,
            focusOffset: highlightData.focusOffset || 0,
            pageNumber: null,
            container: highlightData.container,
            preContent: "", 
            postContent: "", 
            content: eventOrParams.detail.content || window.getSelection().toString().trim() || "",
            originalId: eventOrParams.detail.originalId,
            url: eventOrParams.detail.url || window.location.href
        };
        try {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const { preContent, postContent } = TextFragmentUtils.generatePreAndPostContent(selection);
                highlight.preContent = preContent;
                highlight.postContent = postContent;
            }
        } catch (e) {
            console.warn("Could not generate pre/post content:", e);
        }
    }
    showHighlighterModal = true;
    dispatch("open");

    }

    async function initializeKnowledgeTriggers() {  
        cleanupAllTriggers(); 
             
        if ($PreferencesStoreInitialized && $PreferencesStore?.betaFeatures) {
            await showKnowledgeTriggers();
        }
    }

    async function showKnowledgeTriggers() {
        const triggers = await KnowledgeTriggerStore.fetchTriggers(location.href, document);
        knowledgeTriggers = triggers ?? [];
        const url = window.location.origin + window.location.pathname;
        visualizeSnippets(knowledgeTriggers, url);
    }

    function closeHighlighter() {
        removeSuggestionList();
        showHighlighterModal = false;
        dispatch("close");
    }

    function highlightCreated(event: CustomEvent) {
        showHighlighterModal = false;
        SnippetCreatedModalStore.show(event.detail);
        dispatch("create", event.detail);
    }

    function handleHighlightClicked(event: CustomEvent) {
        const { snippetId } = event.detail;
        const clickedHighlight = HighlightStore.getHighlight(snippetId);
        let group = clickedHighlight?.group;

        if (group === "cw-trigger") {
        
        const triggers = KnowledgeTriggerStore.getTriggers(location.href);
        let  clickedHighlight = triggers.find(trigger => trigger.id === snippetId);

        
        const highlightData = {
            detail: {
                highlight: clickedHighlight.highlight,
                content: clickedHighlight.content,
                originalId: clickedHighlight.originalId,
                url: clickedHighlight.url
            }
        };

        
        showHighlighter(highlightData);
    }
     
    
}
</script>

<ErrorBoundary sentryEnvironment={SentryEnvironment.SHARED} />
{#if shouldRenderHighlighterButton}
    <div
        class="highlighter"
        transition:fade={{ duration: 100, delay: 200 }}
        style="top: {buttonPosition.top}px; left: {buttonPosition.left}px;"
        on:keydown|stopPropagation
        on:keyup|stopPropagation
        on:keypress|stopPropagation
    >
        <button
            class="highlighter__button"
            on:click|stopPropagation|preventDefault={showHighlighter}
            on:mousedown|stopPropagation|preventDefault
            on:mouseup|stopPropagation|preventDefault
        >
            <h3>Save Highlight</h3>
            <p>{shortcutText}</p>
        </button>
    </div>
{/if}

{#if showHighlighterModal && !hideHighlighterButton}
    <HighlighterModal
        {location}
        {highlight}
        on:close={closeHighlighter}
        on:create={highlightCreated}
    />
{/if}

{#if showSnippetCreated}
    <SnippetCreatedModal />
{/if}

<HighlightOptions 
    on:openHighlighter={(e) => showHighlighter(e)}
    on:highlight-clicked={handleHighlightClicked}
/>
<style>
    @import "./shadowdom-reset.css";

    .highlighter {
        @apply absolute z-max flex cursor-pointer select-none bg-transparent;
    }

    .highlighter__button {
        @apply cursor-pointer select-none rounded-md border border-solid border-gray-400 bg-gray-100 px-2 py-2 sm:py-1 text-center;
    }

    .highlighter__button h3 {
        @apply m-0 select-none text-sm font-semibold text-gray-500;
    }

    .highlighter__button p {
        @apply m-0 hidden select-none text-2xs font-light leading-snug text-gray-500 sm:block;
    }
</style>

